import React, { useContext, useState, useEffect } from "react";
import { mdiAdjust, mdiCrosshairsGps, mdiSelectionDrag, mdiDownload } from "@mdi/js";
import intl from "react-intl-universal";
import styled from "styled-components";

import { Plot } from "../../../../../../script/plot";
import commonUtils from "../../../../../../script/utils/commonUtils";
import wattwaechterRest from "../../../../../../rest/wattwaechterRest";
import calcAreaUtils from "../../../../../../script/utils/calcAreaUtils";
import fieldArrayResultUtils from "../../../../../../script/utils/fieldArrayResultUtils";
import plotUtils from "../../../../../../script/utils/plotUtils";
import calculationRest from "../../../../../../rest/calculationRest";

import { Legend } from "../../Legend";
import { ResultViewType } from "../ResultViewType";
import { Grid } from "../../../../../components/Grid";
import { CalcSettingsForm } from "./CalcSettingsForm";
import { Dialog } from "../../../../../containers/Dialog";
import { Spacer } from "../../../../../components/Spacer";
import { Button } from "../../../../../components/Button";
import { DialogBody } from "../../../../../containers/DialogBody";
import { EntryContext } from "../../../../../contexts/EntryContext";
import { useSaveEntry } from "../../../useSaveEntry";

const ContentStyle = styled.div`
    gap: 20px;
    display: flex;
    overflow: hidden;
    height: calc(100vh - 400px);
`;

const ValidationErrorStyle = styled.div`
    color: ${props => props.theme.redDarker};
    font-size: 12px;
`;

const LeftStyle = styled.div`
    display: flex;
    width: 100%;
    overflow: hidden;
    flex-direction: column;
    .legend {
        position: relative;
        > div {
            font-size: 11px!important;
            margin: -12px 15px 1px 15px!important;
        }
    }
`;

const CanvasStyle = styled.div`
    display: flex;
    flex: 1;
    flex-direction: column;
    overflow: hidden;
    border-radius: 2px;
    background-color: ${props => props.theme.white};
    border: 1px solid ${props => props.theme.greyLight};
`;

const RightStyle = styled.div`
    display: flex;
    width: 500px;
    flex-direction: column;
`;

const MovementButtonStyle = styled.div`
    position: relative;
    width: 100%;
    > button {
        width: 100%;
    }
`;

const ActiveDotStyle = styled.div`
    position: absolute;
    width: 6px;
    height: 6px;
    background-color: ${props => (props.$active ? props.theme.greenLight : props.theme.black)};
    border-radius: 50%;
    bottom: 4px;
    right: 4px;
    z-index: 10;
    box-shadow: 0 4px 8px rgba(0,0,0,0.3);
`;

export const WattwaechterResultDialog = props => {
    const { entry, calcSetting, calculation, calculations } = props;

    const entryContext = useContext(EntryContext);

    const [plot, setPlot] = useState();

    const [loadingSlice, setLoadingSlice] = useState(false);
    const [legendData, setLegendData] = useState();
    const [pointSelectionActive, setPointSelectionActive] = useState(false);
    const [selectedPoint, setSelectedPoint] = useState(false);
    const [validationError, setValidationError] = useState(null);

    const {saving, saveEntry} = useSaveEntry();

    const layerName = "wattwaechter-side-view";
    const pointLayerName = "wattwaechter-side-view-point";
    const canvasId = `${layerName}-canvas-${calculation.id}`;

    const [selection, setSelection] = useState(false);
    const [imageSetting, setImageSetting] = useState(structuredClone(calcSetting.imageSetting));
    const [changeableFieldType, setChangeableFieldType] = useState(
        calcSetting.fieldType === "EXHAUSTION_FACTOR_BLMSCHV"
            ? "EXHAUSTION_FACTOR_BLMSCHV"
            : null
    );

    const dialogTitle = (() => {
        const { calcArea } = calculation;
        let title = "";
        if (calcArea.vaxisX !== undefined && calcArea.vaxisY !== undefined && calcArea.vaxisZ !== undefined) {
            const inclination = calcAreaUtils.calculateInclination(calcArea);
            if (inclination === 0) {
                title = intl.get("entry.plan.wattwaechter.resultDialog.topView");
            } else if (inclination === 90) {
                title = intl.get("entry.plan.wattwaechter.resultDialog.sideView");
            } else {
                title = intl.get("entry.plan.wattwaechter.resultDialog.angleView", { inclination });
            }
        }
        return title;
    })();

    const onResultClicked = opt => {
        const layer = plot.getLayer(pointLayerName);
        layer.removeObjects();
        plot.addCircle(layer, opt.scenePoint.x, opt.scenePoint.y, true);
        const {ratioU, ratioV} = plotUtils.calculteAreaSidesClickRatio(opt);
        setSelectedPoint({area: calculation.calcArea, ratioU, ratioV});
    };

    const startPointAnalysis = async () => {
        const currentEntry = entryContext.getEntry();
        if (commonUtils.hasRadioWithoutOperator(currentEntry)) {
            setValidationError(intl.get("exception.missingOperator"));
            return;
        }
        setValidationError(null);
        await saveEntry(currentEntry);
        const {area, ratioU, ratioV} = selectedPoint;
        const newEntry = await calculationRest.wattwaechterPoint(currentEntry.id, area.id, ratioU, ratioV, imageSetting.slice);
        entryContext.setEntry(newEntry);
        props.onClose();
    };

    useEffect(() => {
        const p = new Plot(canvasId, entry.id);
        setPlot(p);
        p.addLayer(pointLayerName, 180);
        p.addLayer(layerName, 140);
    }, []);

    useEffect(() => {
        if (plot) {
            loadSlice(imageSetting);
            loadColorBar(calculation.id, imageSetting);
        }
    }, [plot]);

    useEffect(() => {
        const layer = plot?.getLayer(layerName);
        if (layer) {
            if(pointSelectionActive) {
                const mouseHandler = opt => {
                    if (opt?.target?.type === "image" && pointSelectionActive) {
                        onResultClicked(opt);
                    }
                };
                layer.setHandler("mouse:down", mouseHandler);
            } else {
                const feature = plot.applyRectFeature(layer, "rgba(0,0,255,0.3)", false);
                feature.onSelect(rect => setSelection(rect));
                layer.activate();
            }
        }
        return () => layer?.removeHandler("mouse:down");
    }, [plot, pointSelectionActive]);

    const loadColorBar = async (calculationId, imgSettings) => {
        plot.removeColorBar();
        if (calculationId) {
            const { colorScaleLowerLimit, colorScaleUpperLimit } = imgSettings;
            if (!commonUtils.isBlank(colorScaleLowerLimit) && !commonUtils.isBlank(colorScaleUpperLimit)) {
                const colorBarData = await wattwaechterRest.getColorBar(calculationId, imgSettings, changeableFieldType);
                plot.addColorBar(colorBarData);
            }
        }
    };

    const loadSlice = async imgSettings => {
        const layer = plot.getLayer(layerName);
        layer.removeObjects();
        setLoadingSlice(true);
        const result = await wattwaechterRest.getSlice(calculation.id, imgSettings, ResultViewType.SIDE, changeableFieldType);
        await drawImage(layer, calculation, imgSettings, result);
        setLoadingSlice(false);
    };

    const onDownloadCanvas = () => {
        const jpegUrl = plot.toPngDataUrl();
        commonUtils.download(jpegUrl, entry.name + ".png");
        setSelection(undefined);
    };

    const onDownloadSelection = () => {
        const jpegUrl = plot.toPngDataUrl(selection);
        commonUtils.download(jpegUrl, entry.name + ".png");
        setSelection(undefined);
    };

    const drawImage = async (layer, calculation, imgSettings, resultData) => {
        const extraLegendData = { slice: imgSettings.slice, fieldMax: imgSettings.fieldMax };
        const pointAnalysisResult = calculation.calcArea.calculations.find(c => c.wattwaechterPointResult)?.wattwaechterPointResult;
        await fieldArrayResultUtils.drawOrthogonalViewResult(
            plot,
            setLegendData,
            layer,
            calculation,
            calculation.calcArea,
            extraLegendData,
            resultData,
            calculation.wattwaechterResult,
            imageSetting.slice,
            pointAnalysisResult,
            calculation.calcArea.wattwaechterCalcPoint,
        );
    };

    const onChange = imageSetting => {
        // setImageSetting(imageSetting);
        loadColorBar(calculations[0].id, imageSetting);
        loadSlice(imageSetting);
        setImageSetting(imageSetting);
    };

    return (
        <Dialog
            width="1100px"
            title={dialogTitle}
            onClose={() => props.onClose()}>
            <DialogBody>
                <ContentStyle>
                    <LeftStyle>
                        <CanvasStyle id="canvas-style">
                            <canvas id={canvasId} />
                        </CanvasStyle>
                        <div className="legend">
                            <Legend
                                legendData={legendData}
                                calcSetting={calcSetting}
                                viewType={ResultViewType.SIDE}
                                plot={plot} />
                        </div>
                    </LeftStyle>
                    <RightStyle>
                        <Grid columns="2">
                            <MovementButtonStyle>
                                <Button
                                    value={intl.get("entry.plan.wattwaechter.pointSelectionActiate")}
                                    svgIcon={mdiCrosshairsGps}
                                    onClick={() => setPointSelectionActive(true)} />
                                <ActiveDotStyle $active={pointSelectionActive}/>
                            </MovementButtonStyle>
                            <MovementButtonStyle>
                                <Button
                                    value={intl.get("entry.plan.wattwaechter.selectionActiate")}
                                    svgIcon={mdiSelectionDrag}
                                    onClick={() => setPointSelectionActive(false)} />
                                <ActiveDotStyle $active={!pointSelectionActive}/>
                            </MovementButtonStyle>
                        </Grid>
                        <Spacer size="small" variant={"horizontal"}/>
                        {validationError &&
                            <ValidationErrorStyle>{validationError}</ValidationErrorStyle>
                        }
                        <Grid columns="1">
                            <Button
                                simulateLoading
                                disabled={!selectedPoint || saving || calcSetting.fieldType === "PROTECTION_AREA_BLMSCHV"}
                                svgIcon={mdiAdjust}
                                onClick={() => startPointAnalysis()}
                                value={intl.get("entry.plan.wattwaechter.analysePoint")} />
                            <Button
                                simulateLoading
                                svgIcon={mdiDownload}
                                onClick={() => onDownloadCanvas()}
                                value={intl.get("entry.plan.download.downloadCanvas")} />
                            <Button
                                simulateLoading
                                svgIcon={mdiDownload}
                                disabled={!selection}
                                onClick={() => onDownloadSelection()}
                                value={intl.get("entry.plan.download.downloadSelection")} />
                        </Grid>
                        <Spacer variant="horizontal" />
                        <CalcSettingsForm
                            onChange={onChange}
                            disabled={loadingSlice}
                            imageSetting={imageSetting}
                            sliceNum={calcSetting.sliceNum}
                            fieldType={calcSetting.fieldType}
                            fMin={calculation?.wattwaechterResult?.fmin ?? undefined}
                            fMax={calculation?.wattwaechterResult?.fmax ?? undefined}
                            calcAreaName={calculation.calcArea?.name}
                            onChangeFieldType={setChangeableFieldType} />
                    </RightStyle>
                </ContentStyle>
            </DialogBody>
        </Dialog>
    );
};
